import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MessageSquare, X, Send, Loader2, Sparkles, RefreshCw,
  CheckCircle, Copy, ChevronDown, ChevronUp, Lightbulb,
  AlertTriangle, Shield, Link2, ListChecks, Target, Layers,
  Wand2, Bot, User, Paperclip, FileText, History, Trash2
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { WidgetErrorBoundary } from './ErrorBoundary';

// Storage key for chat history
const CHAT_STORAGE_KEY = 'scopeops_copilot_history';

// ==========================================
// QUICK ACTIONS CONFIG
// ==========================================

const QUICK_ACTIONS = [
  { id: 'rewrite', label: 'Rewrite for clarity', icon: Lightbulb, color: 'text-amber-600' },
  { id: 'suggest_ac', label: 'Suggest AC', icon: ListChecks, color: 'text-green-600' },
  { id: 'identify_risks', label: 'Identify risks', icon: AlertTriangle, color: 'text-red-600' },
  { id: 'identify_constraints', label: 'Find constraints', icon: Shield, color: 'text-blue-600' },
  { id: 'identify_dependencies', label: 'Find dependencies', icon: Link2, color: 'text-purple-600' },
  { id: 'explain_quality', label: 'Explain quality', icon: Target, color: 'text-cyan-600' },
];

// ==========================================
// MESSAGE COMPONENT
// ==========================================

const ChatMessage = ({ message, onCopy, onApply }) => {
  const isUser = message.role === 'user';
  
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
        isUser ? 'bg-purple-100' : 'bg-gradient-to-br from-purple-500 to-indigo-600'
      }`}>
        {isUser ? (
          <User className="w-4 h-4 text-purple-600" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>
      
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block p-3 rounded-xl ${
          isUser 
            ? 'bg-purple-600 text-white rounded-br-sm' 
            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
        }`}>
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
        
        {!isUser && (
          <div className="flex items-center gap-2 mt-1.5">
            <button
              onClick={() => onCopy(message.content)}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
            {message.suggestion && (
              <button
                onClick={() => onApply(message.suggestion)}
                className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
              >
                <CheckCircle className="w-3 h-3" /> Apply
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// MAIN CO-PILOT PANEL
// ==========================================

const ScopingCoPilot = ({
  isOpen,
  onClose,
  tenantId,
  projectId,
  selectedRequirement,
  onApplySuggestion,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load chat history from localStorage on mount
  useEffect(() => {
    if (projectId) {
      const stored = localStorage.getItem(`${CHAT_STORAGE_KEY}_${projectId}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setMessages(parsed.messages || []);
        } catch (e) {
          console.error('Failed to load chat history:', e);
        }
      }
    }
  }, [projectId]);

  // Save chat history to localStorage when messages change
  useEffect(() => {
    if (projectId && messages.length > 0) {
      localStorage.setItem(`${CHAT_STORAGE_KEY}_${projectId}`, JSON.stringify({
        messages,
        lastUpdated: new Date().toISOString(),
      }));
    }
  }, [messages, projectId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Add welcome message when requirement changes
  useEffect(() => {
    if (selectedRequirement && isOpen) {
      const welcomeMsg = {
        role: 'assistant',
        content: `I'm ready to help with "${selectedRequirement.name}". What would you like to do?\n\nTry quick actions below or ask me anything about this requirement.`,
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMsg]);
    }
  }, [selectedRequirement?.id, isOpen]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      const response = await api.post('/api/ai-assist/scoping/conversation', {
        tenant_id: tenantId,
        user_prompt: input.trim(),
        requirement_id: selectedRequirement?.id,
        requirement_name: selectedRequirement?.name,
        requirement_description: selectedRequirement?.description,
        requirement_category: selectedRequirement?.category,
        hierarchy_type: selectedRequirement?.hierarchy_type,
        context_type: 'general',
        // Include document context if available
        document_context: uploadedFile?.fullText?.slice(0, 3000),
        conversation_history: messages.slice(-5).map(m => ({
          role: m.role,
          content: m.content,
        })),
      });
      
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Co-Pilot error:', err);
      toast.error('Failed to get response');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, tenantId, selectedRequirement, messages, uploadedFile]);

  const handleQuickAction = useCallback(async (actionId) => {
    if (!selectedRequirement || loading) return;
    
    const action = QUICK_ACTIONS.find(a => a.id === actionId);
    if (!action) return;
    
    const userMessage = {
      role: 'user',
      content: `${action.label} for this requirement`,
      timestamp: new Date().toISOString(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    
    try {
      const response = await api.post('/api/ai-assist/scoping/quick-action', null, {
        params: {
          tenant_id: tenantId,
          action: actionId,
          requirement_name: selectedRequirement.name,
          requirement_description: selectedRequirement.description || '',
          requirement_category: selectedRequirement.category || '',
        }
      });
      
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        suggestion: {
          type: actionId,
          content: response.data.response,
          requirement_id: selectedRequirement.id,
        }
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Quick action error:', err);
      toast.error('Failed to execute action');
    } finally {
      setLoading(false);
    }
  }, [selectedRequirement, loading, tenantId]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleApply = (suggestion) => {
    onApplySuggestion?.(suggestion);
  };

  const handleClear = () => {
    setMessages([]);
    setUploadedFile(null);
    if (projectId) {
      localStorage.removeItem(`${CHAT_STORAGE_KEY}_${projectId}`);
    }
    if (selectedRequirement) {
      setMessages([{
        role: 'assistant',
        content: `Ready to help with "${selectedRequirement.name}". What would you like to do?`,
        timestamp: new Date().toISOString(),
      }]);
    }
  };

  // Handle file upload for AI analysis
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    const allowedTypes = ['pdf', 'docx', 'doc', 'txt'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error('Please upload a PDF, Word, or text file');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB.');
      return;
    }
    
    setUploading(true);
    setUploadedFile({ name: file.name, status: 'uploading' });
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenant_id', tenantId);
      formData.append('extraction_mode', 'summary');
      
      const response = await api.uploadSpec(formData);
      
      // Add the document context to chat
      const docSummary = response.data.raw_text_preview || 'Document uploaded successfully';
      setUploadedFile({ 
        name: file.name, 
        status: 'ready',
        preview: docSummary.slice(0, 500),
        fullText: docSummary,
      });
      
      const assistantMessage = {
        role: 'assistant',
        content: `I've analyzed "${file.name}". Here's what I found:\n\n${docSummary.slice(0, 800)}${docSummary.length > 800 ? '...' : ''}\n\nYou can now ask me questions about this document or use it to help define requirements.`,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      toast.success('Document uploaded and analyzed');
    } catch (err) {
      console.error('File upload error:', err);
      setUploadedFile(null);
      toast.error('Failed to process document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
  };

  if (!isOpen) return null;

  return (
    <WidgetErrorBoundary widgetName="Scoping Co-Pilot">
      <div 
        className="fixed right-0 top-0 h-full w-[400px] bg-white shadow-2xl z-40 flex flex-col border-l border-gray-200"
        data-testid="scoping-copilot-panel"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-white">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Scoping Co-Pilot</h3>
                <p className="text-xs text-white/70">PMO-Safe Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleClear}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                title="Clear chat"
              >
                <RefreshCw className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Context indicator */}
        {selectedRequirement && (
          <div className="px-4 py-2 bg-purple-50 border-b flex items-center gap-2">
            <Target className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-700 font-medium truncate">
              {selectedRequirement.name}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              selectedRequirement.hierarchy_type === 'theme' ? 'bg-purple-100 text-purple-700' :
              selectedRequirement.hierarchy_type === 'epic' ? 'bg-blue-100 text-blue-700' :
              'bg-green-100 text-green-700'
            }`}>
              {selectedRequirement.hierarchy_type}
            </span>
          </div>
        )}

        {/* Quick Actions */}
        <div className="px-4 py-3 border-b bg-gray-50">
          <button
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="flex items-center justify-between w-full text-sm text-gray-600"
          >
            <span className="flex items-center gap-2 font-medium">
              <Wand2 className="w-4 h-4" />
              Quick Actions
            </span>
            {showQuickActions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {showQuickActions && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              {QUICK_ACTIONS.map(action => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action.id)}
                    disabled={!selectedRequirement || loading}
                    className="flex items-center gap-2 px-3 py-2 text-xs text-left bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Icon className={`w-3.5 h-3.5 ${action.color}`} />
                    <span className="text-gray-700">{action.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <Bot className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">
                {selectedRequirement 
                  ? 'Ready to help! Use quick actions or ask a question.'
                  : 'Select a requirement to get started.'}
              </p>
            </div>
          ) : (
            messages.map((msg, i) => (
              <ChatMessage
                key={i}
                message={msg}
                onCopy={handleCopy}
                onApply={handleApply}
              />
            ))
          )}
          
          {loading && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              </div>
              <div className="bg-gray-100 rounded-xl px-4 py-2">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <span>Thinking</span>
                  <span className="animate-pulse">...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t bg-gray-50">
          {/* Uploaded file indicator */}
          {uploadedFile && (
            <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
              <FileText className="w-4 h-4 text-purple-600" />
              <span className="text-sm text-purple-700 flex-1 truncate">{uploadedFile.name}</span>
              {uploadedFile.status === 'uploading' ? (
                <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
              ) : (
                <button 
                  onClick={removeUploadedFile}
                  className="p-1 hover:bg-purple-100 rounded"
                >
                  <X className="w-3 h-3 text-purple-600" />
                </button>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-2">
            {/* File upload button */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.txt"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="copilot-file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || loading}
              className="p-2.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors disabled:opacity-50"
              title="Upload document for AI analysis"
              data-testid="copilot-upload-btn"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={
                uploadedFile 
                  ? "Ask about the uploaded document..." 
                  : selectedRequirement 
                    ? "Ask about this requirement..." 
                    : "Upload a document or select a requirement..."
              }
              disabled={loading || uploading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              data-testid="copilot-input"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading || uploading}
              className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="copilot-send-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-400">
              PMO-governance only • No commercial advice
            </p>
            {messages.length > 0 && (
              <button
                onClick={handleClear}
                className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" />
                Clear chat
              </button>
            )}
          </div>
        </div>
      </div>
    </WidgetErrorBoundary>
  );
};

export default ScopingCoPilot;
